import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { DateField } from "@/components/ui/date-field";

const PAYMENT_METHODS = [
  { value: "", label: "Non renseigne" },
  { value: "cb", label: "Carte bancaire" },
  { value: "virement", label: "Virement" },
  { value: "especes", label: "Espèces" },
  { value: "cheque", label: "Chèque" },
  { value: "prélèvement", label: "Prélèvement" },
  { value: "autre", label: "Autre" },
];

export function InvoiceForm({ form, u, typeMap, recalcTva }) {
  return (
    <div className="space-y-3" data-testid="invoice-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Type</label>
          <select className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={form.type} onChange={(e) => u("type", e.target.value)} data-testid="field-type">
            {Object.entries(typeMap).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Date</label>
          <DateField value={form.date} onChange={(e) => u("date", e.target.value)} className="h-9 text-sm" data-testid="field-date" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
        <Input value={form.description} onChange={(e) => u("description", e.target.value)}
          placeholder="Ex: Dejeuner client, Plein essence..." className="h-9 text-sm" data-testid="field-description" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">HT</label>
          <Input type="number" step="0.01" value={form.total_ht} onChange={(e) => u("total_ht", e.target.value)}
            onBlur={recalcTva} placeholder="0.00" className="h-9 text-sm" data-testid="field-total-ht" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">TVA %</label>
          <Input type="number" step="0.1" value={form.tva_rate} onChange={(e) => u("tva_rate", e.target.value)}
            onBlur={recalcTva} placeholder="20" className="h-9 text-sm" data-testid="field-tva-rate" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">TVA</label>
          <Input type="number" step="0.01" value={form.tva_amount} onChange={(e) => u("tva_amount", e.target.value)}
            placeholder="0.00" className="h-9 text-sm" data-testid="field-tva-amount" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">TTC</label>
          <Input type="number" step="0.01" value={form.total_ttc} onChange={(e) => u("total_ttc", e.target.value)}
            placeholder="0.00" className="h-9 text-sm font-semibold" data-testid="field-total-ttc" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Paiement</label>
          <select className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={form.payment_method} onChange={(e) => u("payment_method", e.target.value)} data-testid="field-payment-method">
            {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Fournisseur <span className="text-slate-400 font-normal">(optionnel)</span></label>
          <Input value={form.supplier_name} onChange={(e) => u("supplier_name", e.target.value)}
            placeholder="Nom si connu" className="h-9 text-sm" data-testid="field-supplier-name" />
          {form.supplier_name && (
            <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer" data-testid="create-supplier-toggle">
              <input type="checkbox" checked={form.create_supplier !== false}
                onChange={(e) => u("create_supplier", e.target.checked)}
                className="rounded border-slate-300 h-3.5 w-3.5" />
              <span className={`text-[11px] ${form.create_supplier === false ? "text-slate-400" : "text-indigo-600"}`}>
                {form.create_supplier === false ? "Fournisseur existant" : "Ajouter aux fournisseurs"}
              </span>
            </label>
          )}
        </div>
      </div>
      <details className="group">
        <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 select-none" data-testid="more-fields-toggle">
          Plus d'options...
        </summary>
        <div className="mt-2 space-y-2">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">N facture</label>
            <Input value={form.invoice_number} onChange={(e) => u("invoice_number", e.target.value)}
              placeholder="Numéro" className="h-8 text-sm" data-testid="field-invoice-number" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
            <textarea value={form.notes} onChange={(e) => u("notes", e.target.value)}
              placeholder="Notes internes..." rows={2}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm resize-none" data-testid="field-notes" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_paid} onChange={(e) => u("is_paid", e.target.checked)}
              className="rounded border-slate-300" data-testid="field-is-paid" />
            <span className="text-xs text-slate-600">Facture payée</span>
          </label>
        </div>
      </details>
    </div>
  );
}

export function FormButtons({ onClose, onSave, saving, label }) {
  return (
    <div className="flex gap-2 justify-end pt-1">
      <Button variant="outline" size="sm" onClick={onClose} data-testid="cancel-btn">Annuler</Button>
      <Button size="sm" onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-invoice-btn">
        {saving && <Loader2 size={13} className="animate-spin mr-1" />}
        {label}
      </Button>
    </div>
  );
}
